import {
  Code2,
  Network,
  ShieldCheck,
  Cloud,
  Cpu,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { SectionTag } from "./About";
import WhatsAppButton from "./WhatsAppButton";

const ICONS = [Code2, Network, ShieldCheck, Cloud, Cpu];

export default function Services() {
  const { t } = useLang();

  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#050B14]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="max-w-3xl">
          <SectionTag>{t.services.tag}</SectionTag>
          <h2
            className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-[1.1]"
            data-testid="services-title"
          >
            {t.services.title}
          </h2>
          <p className="mt-5 text-slate-200 text-base sm:text-lg max-w-2xl leading-[1.75]">
            {t.services.sub}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[rgba(0,240,255,0.12)] border border-[rgba(0,240,255,0.12)]">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i] || Code2;
            return (
              <div
                key={i}
                className="group relative bg-[#050B14] hover:bg-[#08152a] p-8 sm:p-10 transition-colors"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-end">
                  <ArrowUpRight
                    size={18}
                    className="text-slate-500 group-hover:text-[#00F0FF] transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>

                <div className="mt-2 w-12 h-12 inline-flex items-center justify-center border border-[rgba(0,240,255,0.25)] text-[#00F0FF] group-hover:border-[#00F0FF] group-hover:shadow-[0_0_20px_rgba(0,240,255,0.35)] transition-all">
                  <Icon size={22} />
                </div>

                <h3 className="font-display mt-6 text-xl sm:text-2xl text-white font-semibold leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-slate-300 text-sm sm:text-[15px] leading-[1.75]">
                  {s.desc}
                </p>

                <span className="absolute top-0 start-0 w-3 h-3 border-t border-s border-[#00F0FF] opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="absolute bottom-0 end-0 w-3 h-3 border-b border-e border-[#00F0FF] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            );
          })}

          {/* CTA card to fill the 6th cell — keeps grid balanced */}
          <div
            className="relative bg-gradient-to-br from-[#0A1428] to-[#08152a] p-8 sm:p-10 flex flex-col justify-between"
            data-testid="service-card-cta"
          >
            <div>
              <div className="w-12 h-12 inline-flex items-center justify-center bg-[#00F0FF] text-[#050B14]">
                <Sparkles size={22} />
              </div>
              <h3 className="font-display mt-6 text-xl sm:text-2xl text-white font-semibold leading-snug">
                {t.services.cta_card_title}
              </h3>
              <p className="mt-3 text-slate-300 text-sm sm:text-[15px] leading-[1.75]">
                {t.services.cta_card_desc}
              </p>
            </div>

            <Link
              to="/contact"
              data-testid="service-cta-link"
              className="mt-8 inline-flex items-center gap-3 self-start bg-[#00F0FF] text-[#050B14] hover:bg-white transition-colors px-5 py-3 text-xs font-bold uppercase tracking-[0.16em]"
            >
              {t.services.cta_card_btn}
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>

        {/* WhatsApp block below the services section */}
        <div
          className="mt-12 border border-[#25D366]/25 bg-[#0A1428] p-7 sm:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          data-testid="services-whatsapp-block"
        >
          <div className="max-w-2xl">
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#25D366]">
              WhatsApp
            </p>
            <h3 className="font-display mt-2 text-xl sm:text-2xl text-white font-semibold">
              {t.services.whatsapp_block_title}
            </h3>
            <p className="mt-2 text-slate-300 text-sm sm:text-[15px] leading-[1.75]">
              {t.services.whatsapp_block_desc}
            </p>
          </div>
          <WhatsAppButton
            testId="services-whatsapp-btn"
            size="lg"
            className="shrink-0"
          />
        </div>
      </div>
    </section>
  );
}
